/**
 * Central router for incoming WebSocket messages in Aegix One.
 *
 * Subscribes once to the multiplexed socket and dispatches each message
 * to the appropriate domain handler. Must live under both
 * `SocketProvider` and the React Query provider.
 */

import { useCallback, useMemo, type PropsWithChildren } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser, type AllauthUser } from '@aegix/auth-identity';
import { useSocketMessage } from './SocketContext';
import {
  createSocketHandlers,
  parseSocketMessage,
  type HandlerDeps,
} from './socket-handlers';

export function SocketMessageRouter({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const user = useUser<AllauthUser>();

  const deps = useMemo<HandlerDeps>(
    // Coerce lib's `number | string` user id to `number | null`
    // for ONE-specific handlers (see SocketContext for the same dance).
    () => ({
      queryClient,
      currentUserId: typeof user?.id === 'number' ? user.id : null,
    }),
    [queryClient, user?.id],
  );

  const handlers = useMemo(() => createSocketHandlers(deps), [deps]);

  const handleMessage = useCallback(
    (raw: string) => {
      const parsed = parseSocketMessage(raw);
      if (!parsed) return;

      const handler = handlers[parsed.type];
      if (!handler) return;

      try {
        void handler(parsed.data, parsed.raw);
      } catch (error) {
        console.error(
          `[SocketMessageRouter] handler error for ${parsed.type}:`,
          error,
        );
      }
    },
    [handlers],
  );

  useSocketMessage(handleMessage);

  return <>{children}</>;
}

export default SocketMessageRouter;
